import { useTranslation } from '../../../lib/i18n';
import { timeAgo } from '../../../lib/notification-time';
import { cronLabel, routineActivity, type RoutineActivity } from '../../../lib/routine-schedule';
import type { WebRoutine } from '../../../lib/routines';

// The words a routine is described with, in one place, so the card and the detail modal can never
// disagree about what the same routine's schedule or history says. The rules live in the pure
// `lib/routine-schedule` module; this hook only supplies the translated copy they interpolate.
export function useRoutineCopy() {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language;

  const strings = {
    everyDay: t('routines.schedule.everyDay'),
    weekdays: t('routines.schedule.weekdays'),
    weekends: t('routines.schedule.weekends'),
    everyDow: t('routines.schedule.everyDow'),
    dayOfMonth: t('routines.schedule.dayOfMonth'),
  };

  /** "Every day · 9:00 AM", or the trigger/no-schedule phrasing when there is no describable cron. */
  const scheduleText = (routine: WebRoutine): string => {
    const described = cronLabel(routine.cron, strings, locale);
    if (described) return described;
    if (routine.cron?.trim()) return t('routines.schedule.scheduled');
    if (routine.hasTriggers) return t('routines.schedule.triggerOnly');
    return t('routines.schedule.none');
  };

  /** "Next: Mon, 9:00 AM", or empty when there is no next run to promise. */
  const nextRunText = (routine: WebRoutine): string => {
    if (!routine.nextRun) return '';
    const at = new Date(routine.nextRun);
    if (Number.isNaN(at.getTime())) return '';
    const when = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(at);
    return t('routines.nextRun', { when });
  };

  /**
   * The run history line — or NOTHING when the server did not look far enough back to know.
   *
   * A routine whose rows fell out of the shared action window comes back looking exactly like one that
   * has never run, so "Hasn't run yet" is only ever said on a `runsKnown` finding. See `routineActivity`.
   */
  const activityText = (routine: WebRoutine): string => {
    const activity: RoutineActivity = routineActivity(routine);
    if (activity.kind === 'unknown') return '';
    if (activity.kind === 'never') return t('routines.activity.never');
    const parts = [t('routines.activity.lastRun', { when: timeAgo(activity.lastRun) })];
    if (activity.taskCount > 0) parts.push(t('routines.activity.tasks', { count: activity.taskCount }));
    return parts.join(' · ');
  };

  /**
   * "12 runs" — or "20+ runs" when the count is a floor rather than the answer. Empty when unknown.
   *
   * The gate is the WINDOW, not the last run: a count is rendered whenever the run window was seen
   * (`runsKnown`), with or without a `lastRun`, and never on an `unknown` window — a count over a window
   * we could not look far enough back into is not a count. Same rule as both natives'
   * `routineRunCountLabel` / `RoutineActivity.showsRunCount`.
   */
  const runCountText = (routine: WebRoutine): string => {
    if (routineActivity(routine).kind === 'unknown') return '';
    const count = Math.max(0, routine.runCount ?? 0);
    if (count === 0) return '';
    return routine.runCountCapped === true
      ? t('routines.activity.runsCapped', { count })
      : t('routines.activity.runs', { count });
  };

  /**
   * The localized explanation for a stopped routine — the agent's own note when it left one.
   *
   * The keys are the `routines.pauseReason.*` set both phones use (`RoutineDetailScreen.pauseExplanation`
   * / `RoutinePauseReason`); the SENTENCES are web's, which native adopts. One reason, one wording, three
   * surfaces.
   */
  const pauseText = (routine: WebRoutine): string => {
    if (routine.pauseNote?.trim()) return routine.pauseNote.trim();
    switch (routine.pauseReason) {
      case 'OUT_OF_CREDITS':
        return t('routines.pauseReason.outOfCredits');
      case 'PAUSED_BY_USER':
        return t('routines.pauseReason.pausedByUser');
      case 'ONBOARDING_INCOMPLETE':
        return t('routines.pauseReason.onboardingIncomplete');
      case 'NO_AGENTS':
        return t('routines.pauseReason.noAgents');
      default:
        return '';
    }
  };

  const titleOf = (routine: WebRoutine): string => routine.name?.trim() || t('routines.untitled');

  return { scheduleText, nextRunText, activityText, runCountText, pauseText, titleOf };
}
